"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
/**
 * The payments module provides the means to pay for an order using an online payment provider
 * @preferred
 */
var service_1 = require("../service");
var xhr_1 = require("../xhr");
var _ = require("lodash");
/**
 * Provides access to order payment methods available in EVA
 */
var Payments = /** @class */ (function () {
    /**
     * [constructor description]
     * @param  {Order}  order The order to perform the payments for
     * @return {void}         This method doesn't return anything
     */
    function Payments(order) {
        this.order = order;
    }
    /**
     * Returns the list of available payment methods
     * @return {Promise<IEVAServiceResponse<EVA.Core.GetAvailablePaymentMethods, EVA.Core.GetAvailablePaymentMethodsResponse>>} A promise resolving when the fetch from the back-end has completed
     */
    Payments.prototype.getAvailableMethods = function () {
        // Create our service instance
        //
        var service = new service_1.EVAService("GetAvailablePaymentMethods");
        // Build the request data
        // The base class will add things like SessionID so to keep typescript
        // happy we're using an 'any' type object here
        //
        var requestData = {};
        // Only add order ID if it's valid
        //
        var currentOrderId = this.order.getId();
        if (currentOrderId) {
            requestData.OrderID = currentOrderId;
        }
        // Perform the call and capture the resulting promise
        //
        return service.call(requestData);
    };
    Payments.prototype.getMultiSafePaymentGateWays = function (orderId) {
        if (orderId === undefined) {
            orderId = this.order.getId();
        }
        var service = new service_1.EVAService("GetMultiSafepayGateways");
        var requestData = {
            OrderID: orderId
        };
        return service.call(requestData);
    };
    Payments.prototype.getMollyPaymentGateWays = function () {
        var service = new service_1.EVAService("ListMollieGateways");
        var requestData = {};
        return service.call(requestData);
    };
    Payments.prototype.getBuckarooPaymentGateWays = function () {
        var service = new service_1.EVAService("ListBuckarooGateways");
        var requestData = {};
        return service.call(requestData);
    };
    /**
     * Retuns the list of user cards available
     * @return {Promise<IEVAServiceResponse<EVA.Payment.UserCard.GetUserCardsForUser, EVA.Payment.UserCard.GetUserCardsForUserResponse>>} A promise resolving when the fetch from the back-end has completed
     */
    Payments.prototype.listUserCards = function (userID) {
        // Create our service instance
        //
        var service = new service_1.EVAService("GetUserCardsForUser");
        // Build the request data
        // The base class will add things like SessionID so to keep typescript
        // happy we're using an 'any' type object here
        //
        var requestData = {
            UserID: userID
        };
        // Perform the call and capture the resulting promise
        //
        return service.call(requestData);
    };
    /**
     * Pay for the order using a PIN terminal from ADYEN (shop/employee only)
     * @param  {string}  merchantCode     The ADYEN merchant code to use
     * @param  {number}  amount           The amount to pay
     * @return {Promise<IEVAServiceResponse<EVA.Core.CreatePayment, EVA.Core.CreatePaymentResponse>>}   A promise resolving when the fetch from the back-end has completed
     */
    Payments.prototype.payWithAdyen = function (merchantCode, amount) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            // Perform the call and capture the resulting promise
            //
            _this.createPayment("ADYEN", { "MerchantCode": merchantCode }, amount)
                .then(function (result) {
                resolve(result.response);
            })
                .catch(reject);
        });
    };
    /**
     *
     * @param {string} merchantAccount The Adyen merchant account to use.
     * @param {number} adyenChannel    The adyen channel to use. Possible values are: 1 = Web, 2 = iOS, 3 = Android.
     * @param {string} origin          The origin URL
     * @param {string} returnUrl       The return URL
     * @param {string} token           The token to use
     * @param {number} amount          The amount of money to pay
     * @return {Promise<IEVAServiceResponse<EVA.Core.CreatePayment, EVA.Core.CreatePaymentResponse>>}   A promise resolving when the fetch from the back-end has completed
     */
    Payments.prototype.payWithAdyenCheckout = function (merchantAccount, adyenChannel, origin, returnUrl, token, amount) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            // Perform the call and capture the resulting promise
            //
            _this.createPayment("ADYEN_CHECKOUT", {
                MerchantAccount: merchantAccount,
                Channel: adyenChannel,
                Origin: origin,
                ReturnUrl: returnUrl,
                Token: token
            }, amount)
                .then(function (result) { return resolve(result.response); })
                .catch(reject);
        });
    };
    Payments.prototype.payWithAdyenCheckoutSession = function (merchantAccount, adyenChannel, origin, returnUrl, token, amount, version) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            // Perform the call and capture the resulting promise
            //
            _this.createPayment("ADYEN_CHECKOUT_SESSION", {
                MerchantAccount: merchantAccount,
                Channel: adyenChannel,
                Origin: origin,
                ReturnUrl: returnUrl,
                Token: token,
                SdkVersion: version
            }, amount)
                .then(function (result) { return resolve(result.response); })
                .catch(reject);
        });
    };
    Payments.prototype.payWithAdyenHostedPaymentPages = function (merchantAccount, amount) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            // Perform the call and capture the resulting promise
            //
            _this.createPayment("ADYEN_HPP", {
                MerchantAccount: merchantAccount,
            }, amount)
                .then(function (result) { return resolve(result.response); })
                .catch(reject);
        });
    };
    Payments.prototype.payWithMultiSafePay = function (method, returnUrl, amount) {
        var payment;
        payment = this.createMultisafepayPayment({
            GatewayID: method,
            ReturnUrl: returnUrl
        }, amount);
        return payment;
    };
    Payments.prototype.payWithMollie = function (method, returnUrl, amount, issuer) {
        if (method === void 0) { method = ""; }
        var payment;
        payment = this.createMolliePayment({
            GatewayID: method,
            ReturnUrl: returnUrl,
            Issuer: issuer,
        }, amount);
        return payment;
    };
    Payments.prototype.payWithBuckaroo = function (paymentTypeId, method, returnUrl, amount, issuer) {
        if (method === void 0) { method = ""; }
        var payment;
        payment = this.createBuckerooPayment(paymentTypeId, {
            GatewayID: method,
            ReturnUrl: returnUrl,
            Issuer: issuer,
        }, amount);
        return payment;
    };
    /**
     * Pay for the order using iDEAL
     * @param  {string}  returnUrl     The URL to return to when the payment has completed
     * @param  {string}  idealBankName The name of the bank for the ideal payment
     * @return {Promise<IEVAServiceResponse<EVA.Core.CreatePayment, EVA.Core.CreatePaymentResponse>>}   A promise resolving when the fetch from the back-end has completed
     */
    Payments.prototype.payWithIdeal = function (returnUrl, idealBankName) {
        var payment;
        payment = this.createAcapturePayment({
            PaymentBrand: "IDEAL",
            BankAccount: {
                BankName: idealBankName,
                Country: "NL"
            },
            ReturnUrl: returnUrl
        });
        return payment;
    };
    /**
     * Pay for the order using PayPal
     * @param  {string}  returnUrl     The URL to return to when the payment has completed
     * @return {Promise<IEVAServiceResponse<EVA.Core.CreatePayment, EVA.Core.CreatePaymentResponse>>}   A promise resolving when the fetch from the back-end has completed
     */
    Payments.prototype.payWithPaypal = function (returnUrl) {
        return this.createAcapturePayment({
            PaymentBrand: "PAYPAL",
            ReturnUrl: returnUrl
        });
    };
    /**
     * Pay for the order using a MasterCard/Eurocard credit card
     * @param  {string}  returnUrl     The URL to return to when the payment has completed
     * @return {Promise<IEVAServiceResponse<EVA.Core.CreatePayment, EVA.Core.CreatePaymentResponse>>}   A promise resolving when the fetch from the back-end has completed
     */
    Payments.prototype.payWithMasterCard = function (returnUrl) {
        return this.createAcapturePayment({
            PaymentBrand: "MASTER",
            ReturnUrl: returnUrl
        });
    };
    /**
     * Pay for the order using an American Express credit card
     * @param  {string}  returnUrl     The URL to return to when the payment has completed
     * @return {Promise<IEVAServiceResponse<EVA.Core.CreatePayment, EVA.Core.CreatePaymentResponse>>}   A promise resolving when the fetch from the back-end has completed
     */
    Payments.prototype.payWithAmericanExpress = function (returnUrl) {
        return this.createAcapturePayment({
            PaymentBrand: "AMEX",
            ReturnUrl: returnUrl
        });
    };
    /**
     * Pay for the order using a Visa credit card
     * @param  {string}  returnUrl     The URL to return to when the payment has completed
     * @return {Promise<IEVAServiceResponse<EVA.Core.CreatePayment, EVA.Core.CreatePaymentResponse>>}   A promise resolving when the fetch from the back-end has completed
     */
    Payments.prototype.payWithVisa = function (returnUrl) {
        return this.createAcapturePayment({
            PaymentBrand: "VISA",
            ReturnUrl: returnUrl
        });
    };
    /**
     * Pay for an order with an insurance card (HFS Waardebrief)
     * @param {string}  referenceNumber The reference number for the credit payment
     * @param {string}  policyNumber    The policy number for the credit payment
     * @return {Promise<IEVAServiceResponse<EVA.Core.CreatePayment, EVA.Core.CreatePaymentResponse>>}   A promise resolving when the fetch from the back-end has completed
     */
    Payments.prototype.payWithInsuranceCreditPayment = function (referenceNumber, policyNumber) {
        return this.createPayment("HARMONY-INSURANCE", {
            ReferenceNumber: referenceNumber,
            PolicyNumber: policyNumber
        });
    };
    /**
     * Pay for an order with a user card
     * @param {number}  userId          The id of the user
     * @param {number}  userCardId      The id of the userCardId
     * @param {amount}  amount          The amount that will be charged from the usercard
     * @return {Promise<IEVAServiceResponse<EVA.Core.CreatePayment, EVA.Core.CreatePaymentResponse>>}   A promise resolving when the fetch from the back-end has completed
     */
    Payments.prototype.payWithUserCard = function (paymentMethodId, userId, userCardId, amount) {
        return this.createPayment("USERCARD", {
            UserID: userId,
            UserCardID: userCardId,
            CardAmount: amount
        }, amount, paymentMethodId);
    };
    /**
     * Pay for an order with Freebees
     * @param {number}  userId          The id of the user
     * @param {number}  userCardId      The id of the userCardId
     * @param {amount}  amount          The amount that will be charged from the usercard
     * @return {Promise<IEVAServiceResponse<EVA.Core.CreatePayment, EVA.Core.CreatePaymentResponse>>}   A promise resolving when the fetch from the back-end has completed
     */
    Payments.prototype.payWithFreebees = function (cardNumber, pin, amount, pinType) {
        if (pinType === void 0) { pinType = "pin"; }
        var properties = {
            CardNumber: cardNumber,
        };
        if (pinType === "pin") {
            properties.Pin = pin;
        }
        else if (pinType === "password") {
            properties.Password = pin;
        }
        return this.createPayment("FREEBEES", properties, amount);
    };
    Payments.prototype.payWithEvaPay = function (baseUrl, sendEmail, amount) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            // Perform the call and capture the resulting promise
            //
            _this.createPayment("EVAPAY", {
                BaseUrl: baseUrl,
                SendEmail: sendEmail,
            }, amount)
                .then(function (result) { return resolve(result.response); })
                .catch(reject);
        });
    };
    /**
     * Get the balance of the usercard
     *
     * @param {number} cardId
     * @param {string} currencyId
     * @return {Promise<IEVAServiceResponse<EVA.Payment.UserCard.GetUserCardBalance, EVA.Payment.UserCard.GetUserCardBalanceResponse>>}   A promise resolving when the fetch from the back-end has completed
     */
    Payments.prototype.getUserCardBalance = function (cardId, currencyId) {
        var service = new service_1.EVAService("GetCardBalance");
        // Build the service request descriptor
        //
        var requestData = {
            UserCardID: cardId,
            CurrencyID: currencyId,
        };
        return service.call(requestData);
    };
    /**
     * Get the balance of a gifcard
     *
     * @param {string} cardId
     * @param {string} cardPin
     * @param {string} barcode
     * @param {string} cardType
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.CardBalanceCheck, EVA.EVA.Core.Services.CardBalanceCheckResponse>>}   A promise resolving when the fetch from the back-end has completed
     */
    Payments.prototype.cardBalanceCheck = function (cardId, cardPin, barcode, cardType) {
        if (cardType === void 0) { cardType = "INTERSOLVE"; }
        var service = new service_1.EVAService("CardBalanceCheck");
        // Build the service request descriptor
        //
        var request = {
            CardNumber: cardId,
            CardPin: cardPin,
            CardType: cardType,
            Barcode: barcode,
        };
        return service.call(request);
    };
    Payments.prototype.cancelPendingUserCardPayment = function (paymentTransactionID) {
        var service = new service_1.EVAService("CancelPendingUserCardPayment");
        var requestData = {
            PaymentTransactionID: paymentTransactionID,
        };
        return service.call(requestData);
    };
    /**
     * Pay for an order with AfterPay
     * @param  {any}  properties     Optional additional properties to send
     * @return {Promise<IEVAServiceResponse<EVA.Core.CreatePayment, EVA.Core.CreatePaymentResponse>>}   A promise resolving when the fetch from the back-end has completed
     */
    Payments.prototype.payWithAfterPay = function (properties) {
        return this.createPayment("AFTERPAY", properties);
    };
    /**
     * Finalize a payment (usercard needs this)
     * @param {string}  paymentTransactionID    The payment transaction that is to be finalized
     * @return {Promise<IEVAServiceResponse<EVA.Core.FinalizePayment, EVA.Core.FinalizePaymentResponse>>}   A promise resolving when the fetch from the back-end has completed
     */
    Payments.prototype.finalizePayment = function (paymentTransactionID, amount) {
        var service = new service_1.EVAService("FinalizePayment");
        var requestData = {
            PaymentTransactionID: paymentTransactionID,
            Amount: amount
        };
        return service.call(requestData);
    };
    /**
     * Pay for an order with a manual payment
     * @return {Promise<IEVAServiceResponse<EVA.Core.CreatePayment, EVA.Core.CreatePaymentResponse>>}   A promise resolving when the fetch from the back-end has completed
     */
    Payments.prototype.payWithManualPayment = function () {
        return this.createPayment("MANUAL", {});
    };
    /**
     * Pay for the order using a PIN terminal (shop/employee only)
     * @param  {string}  stationId     The URL to return to when the payment has completed
     * @return {Promise<IEVAServiceResponse<EVA.Core.CreatePayment, EVA.Core.CreatePaymentResponse>>}   A promise resolving when the fetch from the back-end has completed
     */
    Payments.prototype.payWithPIN = function (stationId, amountGiven) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            // Perform the call and capture the resulting promise
            //
            _this.createPayment("PIN", {}, 
            // This is the amount we want to PIN
            //
            Math.max(0, amountGiven))
                .then(function (result) {
                var transaction = result.response.PaymentTransaction;
                // Normal transaction take 30 seconds. For PIN we have to wait for the customer to perform it's action
                // So we increase the wait time to three minutes (180000 milliseconds)
                //
                _this.startTransaction(stationId, transaction.ID, true, 180000).then(resolve).catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * Abort a payment for the order using a PIN terminal (shop/employee only)
     * @param  {string}  stationId     The URL to return to when the payment has completed
     * @return {Promise<IEVAServiceResponse<EVA.Pin.CreatePinPayment, EVA.Pin.CreatePinPaymentResponse>>}   A promise resolving when the fetch from the back-end has completed
     */
    Payments.prototype.abortPIN = function (stationId) {
        return new Promise(function (resolve, reject) {
            // Create our service instance
            //
            var service = new service_1.EVAService("AbortTransaction");
            // Build the request data
            // The base class will add things like SessionID so to keep typescript
            // happy we're using an 'any' type object here
            // We need to explicitly set UseHardwareID to false because it defaults to true
            //
            var requestData = {
                UseHardwareID: false,
                StationID: stationId
            };
            // Perform the call and capture the resulting promise
            //
            return service.call(requestData);
        });
    };
    /**
     * Pay for the order using a Cash drawer (shop/employee only)
     * @parm   {Order}   order         The order that is to be paid
     * @param  {string}  returnUrl     The URL to return to when the payment has completed
     * @return {Promise<IEVAServiceResponse<EVA.Payment.Cash.MakeCashPayment, EVA.Payment.Cash.MakeCashPaymentResponse>>}   A promise resolving when the fetch from the back-end has completed
     */
    Payments.prototype.payWithCash = function (stationId, amountGiven) {
        return this.createPayment("CASH", {
            AmountGiven: Math.max(0, amountGiven),
            StationID: stationId
        });
    };
    /**
     * Pay for an order with a Media Markt method
     * @return {Promise<IEVAServiceResponse<EVA.Core.CreatePayment, EVA.Core.CreatePaymentResponse>>}   A promise resolving when the fetch from the back-end has completed
     */
    Payments.prototype.payWithMediaMarktPayment = function () {
        return this.createPayment("MEDIAMARKT", {});
    };
    /**
     * Pay for the order using Intersolve giftcards
     * @param  {string}  cardNumber     The number on the Intersolve giftcard
     * @param  {string}  [pin]          A giftcard optionally has a PIN, provide when on the card to use the card
     * @return {Promise<IEVAServiceResponse<EVA.Core.CreatePayment, EVA.Core.CreatePaymentResponse>>}   A promise resolving when the fetch from the back-end has completed
     */
    Payments.prototype.payWithIntersolve = function (returnUrl, cardNumber, pin, amountGiven) {
        return this.createPayment("INTERSOLVE", {
            CardNumber: cardNumber,
            Pin: pin,
            ReturnUrl: returnUrl
        }, amountGiven);
    };
    /**
     * Create a payment for a certain paymentmethod. The available paymentmethods for this specific order can be retrieved using the getAvailableMethods()
     * @param {string}  paymentMethodCode   The code of the payment method to be used to pay for the order, valid values need to be retrieved using getAvailableMethods()
     * @param {any}     properties          Payment method specific properties
     * @param {number}  amount              Optional parameter to specify a specific amount for this payment. If not provided the EVA Core service will default to the open amount of the order
     */
    Payments.prototype.createPayment = function (paymentMethodCode, properties, amount, paymentTypeId) {
        var service = new service_1.EVAService("CreatePayment");
        var requestData = {};
        if (paymentTypeId) {
            requestData = {
                Amount: amount,
                OrderID: this.order.getId(),
                Properties: properties,
                PaymentTypeID: paymentTypeId,
            };
        }
        else {
            requestData = {
                Amount: amount,
                Code: paymentMethodCode,
                OrderID: this.order.getId(),
                Properties: properties,
            };
        }
        return service.call(requestData);
    };
    /**
     * Create a Acapture payment
     * @param {any}      properties         Payment method specific properties
     * @param {number}   amount             Optional parameter to specify a specific amount for this payment. If not provided the EVA Core service will default to the open amount of the order
     */
    Payments.prototype.createAcapturePayment = function (properties) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            _this.createPayment("ACAPTURE", properties)
                .then(function (serviceResult) {
                var success = _.get(serviceResult, "response.Properties.Success");
                if (success) {
                    resolve(serviceResult);
                }
                else {
                    reject(serviceResult);
                }
            })
                .catch(reject);
        });
    };
    Payments.prototype.createMultisafepayPayment = function (properties, amount) {
        return this.createPayment("MULTISAFEPAY", properties, amount);
    };
    Payments.prototype.createMolliePayment = function (properties, amount) {
        return this.createPayment("MOLLIE", properties, amount);
    };
    Payments.prototype.createBuckerooPayment = function (paymentTypeId, properties, amount) {
        return this.createPayment("BUCKAROO", properties, amount, paymentTypeId);
    };
    /**
     * Start a PIN payment transaction
     * @param  {string}     stationId       The station identifier provided the PIN terminal
     * @param  {number}     transactionId   The transaction identifier for the PIN payment
     * @param  {boolean}    forceStart      Force starts the transaction. Defaults to true
     * @param  {number}     timeout         Change the default timeout of the XHR call, default is 30 seconds
     * @return {Promise<IEVAServiceResponse<EVA.Pin.StartTransaction, EVA.Pin.StartTransactionResponse>>}   A promise resolving when the fetch from the back-end has completed
     */
    Payments.prototype.startTransaction = function (stationId, transactionId, forceStart, timeout) {
        var _this = this;
        if (forceStart === void 0) { forceStart = true; }
        if (timeout === void 0) { timeout = 30000; }
        // Create our service instance
        //
        var service = new service_1.EVAService("StartTransaction");
        // Build the request data
        // The base class will add things like SessionID so to keep typescript
        // happy we're using an 'any' type object here
        //
        var requestData = {
            PaymentTransactionID: transactionId,
            ForceStart: forceStart,
            UseHardwareID: false,
            StationID: stationId
        };
        // Perform the call and capture the resulting promise
        //
        return service.call(requestData, true, timeout)
            .then(function (response) {
            switch (response.response.ResultStatus) {
                // Only code that indicates that the transaction was successful
                case 3: // Success
                    return response;
                case 17: // ConnectToTerminal
                    var xhr = new xhr_1.XHR();
                    xhr.call({
                        method: xhr_1.EXMLHttpRequestMethod.GET,
                        // This may potentially be running in a browser. Needs to use https:
                        // to fool the browser that it is safe to connect to the given url
                        //
                        url: "https://" + response.response.LocalTerminalAddress,
                        headers: {}
                    })
                        .catch(function () {
                        // Ignore whatever happens, we just want to attempt to connect
                        // to the terminal; that's the best we can do
                        //
                    });
                    // Go at it again because we've done our job
                    //
                    return _this.startTransaction(stationId, transactionId, forceStart);
                default:
                    // Reject the promise because we got a none success code
                    //
                    throw response;
            }
        });
    };
    return Payments;
}());
exports.Payments = Payments;
